import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';

export interface UseUnsavedChangesOptions {
  /**
   * Whether there are unsaved changes
   */
  hasUnsavedChanges: boolean;
  /**
   * Callback when navigation is blocked
   */
  onBlock?: () => void;
  /**
   * Custom message for beforeunload event
   */
  beforeUnloadMessage?: string;
}

export interface UseUnsavedChangesReturn {
  /**
   * Whether navigation is currently blocked
   */
  isBlocked: boolean;
  /**
   * Function to proceed with navigation (unblock)
   */
  proceed: () => void;
  /**
   * Function to cancel navigation (stay on page)
   */
  cancel: () => void;
}

/**
 * Hook to handle unsaved changes protection
 * Blocks navigation via React Router and browser refresh/close
 */
export const useUnsavedChanges = ({
  hasUnsavedChanges,
  onBlock,
  beforeUnloadMessage = 'Dikkat! Kaydedilmemiş değişiklikler var. Sayfadan ayrılmak istediğinizden emin misiniz?',
}: UseUnsavedChangesOptions): UseUnsavedChangesReturn => {
  // Use React Router's useBlocker to intercept route navigation
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
  );

  // Handle beforeunload event for browser refresh/close
  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Modern browsers ignore custom messages, but we can still show a default dialog
      e.preventDefault();
      e.returnValue = beforeUnloadMessage;
      return beforeUnloadMessage;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, beforeUnloadMessage]);

  // Handle React Router navigation blocking - call onBlock when navigation is blocked
  useEffect(() => {
    if (blocker.state === 'blocked') {
      onBlock?.();
    }
  }, [blocker.state, onBlock]);

  // Auto-proceed if unsaved changes are cleared while blocked
  useEffect(() => {
    if (!hasUnsavedChanges && blocker.state === 'blocked') {
      blocker.proceed();
    }
  }, [hasUnsavedChanges, blocker]);

  const proceed = () => {
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  };

  const cancel = () => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  };

  return {
    isBlocked: blocker.state === 'blocked',
    proceed,
    cancel,
  };
};

